'use client'

import { useContext, useState } from 'react'
import { Modal, message, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import Upload from 'antd/es/upload'
import { getPresignedS3Url, updateProfile } from '@/src/api'

import type { UploadFile } from 'antd/es/upload/interface'
import type { RcFile, UploadProps } from 'antd/es/upload'
import { trackProfilePhotoEvents } from '@/src/lib/analytics/events'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'

interface ProfilePhotoModalProps {
  isModalOpen: boolean
  handleOk: (file?: RcFile) => void
  handleCancel: () => void
  apiData: any
  setApiData: (data: any) => void
  setProfile: (callback: (prev: any) => any) => void
}

const { Dragger } = Upload
const { Text } = Typography

export const ProfilePhotoModal = ({
  isModalOpen,
  handleOk,
  handleCancel,
  apiData,
  setApiData,
  setProfile,
}: ProfilePhotoModalProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [messageApi, contextHolder] = message.useMessage()
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error('You can only upload image files!')
      return false
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!')
      return false
    }
    return false // Prevent auto upload, we'll handle it manually
  }

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  const handleModalOk = async () => {
    if (fileList.length > 0 && fileList[0].originFileObj) {
      const file = fileList[0].originFileObj
      try {
        // Get presigned URL and upload to S3
        const url = await getPresignedS3Url(file)
        if (url) {
          // Update API data with new profile picture URL
          const updatedApiData = {
            ...apiData,
            profile_picture: url,
          }

          // Update profile in API
          const response = await updateProfile(updatedApiData)
          if (response.status === 'success') {
            // Update local state
            setApiData(updatedApiData)
            setProfile((prev) => ({
              ...prev,
              profile_picture: url,
            }))
            messageApi.success('Profile photo updated successfully')
            handleOk(file)
            trackProfilePhotoEvents.photoUpdated(trackAmplitudeEvent)({
              'Photo Updated': file.name,
              'Profile Updated': updatedApiData,
            })
            handleCancel()
          } else {
            messageApi.error(
              response.message || 'Failed to update profile photo'
            )
            handleCancel() // Close modal on error
          }
        }
      } catch (error) {
        console.error('Error uploading photo:', error)
        messageApi.error('Failed to upload profile photo')
        handleCancel() // Close modal on error
      }
    } else {
      messageApi.error('Please select an image to upload')
      handleCancel() // Close modal when no file is selected
    }
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <span className='font-bold text-lg text-primary'>
            Change Profile Photo
          </span>
        }
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleCancel}
        okText='Upload'
        cancelText='Cancel'
      >
        <div className='w-full flex flex-col items-center justify-center gap-4'>
          <Dragger
            name='avatar'
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={handleChange}
            maxCount={1}
            accept='image/*'
            className='w-full'
          >
            {fileList.length > 0 && fileList[0].originFileObj ? (
              <Image
                src={URL.createObjectURL(fileList[0].originFileObj as Blob)}
                alt='avatar'
                width={200}
                height={200}
                style={{ objectFit: 'contain' }}
              />
            ) : (
              <div className='flex flex-col items-center justify-center w-full h-full'>
                <FontAwesomeIcon
                  icon='arrow-up-from-bracket'
                  className='text-3xl mb-2 text-primary'
                />
                <Text className='font-medium text-center'>
                  Drag and drop
                  <br />
                  your photo here
                </Text>
                <Text
                  type='secondary'
                  className='text-xs text-gray-400 mt-1 text-center'
                >
                  or click to browse
                </Text>
              </div>
            )}
          </Dragger>
          <Text type='secondary' className='text-center'>
            Upload a square photo (max 2MB).
            <br />
            Supported formats: JPG, PNG, GIF
          </Text>
        </div>
      </Modal>
    </>
  )
}
